import React, { useCallback, useState } from 'react';
import {
	ModalRequestDocument,
	ModalSelectUsers,
} from '../components/screens/lawyerapp/cases/case-clients/request-document';
import {
	DocumentRequiredPayloadItem,
	PhaseName,
	RequestDocumentData,
} from '@/interfaces';

interface UseRequestDocumentModalsProps {
	caseName: string;
	phaseName?: PhaseName;
	isCaseReadOnly?: boolean;
	isStepApproved?: boolean;
	isToAllUsers?: boolean;
	onSubmit: (data: RequestDocumentData) => Promise<void>;
	selectedUserIds: (string | null)[];
	setSelectedUserIds: React.Dispatch<React.SetStateAction<(string | null)[]>>;
	existingDocuments?: DocumentRequiredPayloadItem[];
	initialData?: DocumentRequiredPayloadItem | null;
	onCloseRequestDocuments?: () => void;
}

export const useRequestDocumentModals = ({
	caseName,
	phaseName,
	isCaseReadOnly = false,
	isStepApproved = false,
	isToAllUsers = false,
	onSubmit,
	selectedUserIds,
	setSelectedUserIds,
	existingDocuments,
	initialData,
	onCloseRequestDocuments,
}: UseRequestDocumentModalsProps) => {
	const [isOpenSelectUsers, setIsOpenSelectUsers] = useState(false);
	const [isOpenRequestDocuments, setIsOpenRequestDocuments] = useState(false);

	const onOpenRequestDocuments = useCallback(() => {
		if (isToAllUsers) {
			setIsOpenSelectUsers(true);
		} else {
			setIsOpenRequestDocuments(true);
		}
	}, [isToAllUsers]);

	const handleCloseSelectUsers = useCallback(() => {
		setIsOpenSelectUsers(false);
		setSelectedUserIds([]);
	}, []);

	const handleContinueSelectUsers = useCallback(() => {
		setIsOpenSelectUsers(false);
		setIsOpenRequestDocuments(true);
	}, []);

	const handleSetIsOpenRequestDocuments: React.Dispatch<
		React.SetStateAction<boolean>
	> = (value) => {
		setIsOpenRequestDocuments((prev) => {
			const next = typeof value === 'function' ? value(prev) : value;
			if (prev && !next) {
				onCloseRequestDocuments?.();
			}
			return next;
		});
	};

	const renderRequestDocumentModals = (
		<>
			<ModalSelectUsers
				open={isOpenSelectUsers}
				setOpen={setIsOpenSelectUsers}
				selectedUserIds={selectedUserIds}
				setSelectedUserIds={setSelectedUserIds}
				onContinue={handleContinueSelectUsers}
				phaseName={phaseName}
				onClose={handleCloseSelectUsers}
			/>

			<ModalRequestDocument
				open={isOpenRequestDocuments}
				setOpen={handleSetIsOpenRequestDocuments}
				onBack={isToAllUsers ? undefined : () => {}}
				caseName={caseName}
				isCaseReadOnly={isCaseReadOnly}
				isStepApproved={isStepApproved}
				onSubmit={onSubmit}
				existingDocuments={existingDocuments}
				initialData={initialData}
			/>
		</>
	);

	return {
		onOpenRequestDocuments,
		renderRequestDocumentModals,
		selectedUserIds,
		setIsOpenRequestDocuments: handleSetIsOpenRequestDocuments,
	};
};
